import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Checkbox,
    Dimmer,
    Divider,
    Grid,
    Header,
    Loader,
    Message,
    Modal,
    Segment,
} from "semantic-ui-react";
import { format } from "date-fns";
import pluralize from "pluralize";
import { isEmpty } from "lodash";

import { CompanyFeatures } from "utilities/dist/company/constants";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { loadInvoiceRecommendation, toggleRecommendationModal } from "./actions";
import { formatAmount } from "utilities/dist/helpers";
import { transformProductToItem } from "../../product/helpers";
import { DateFormat } from "../helpers";
import { parseServerTimestampForClient } from "../../common/helpers";
import { addInvoiceBuilderItemsFromRecommendation } from "../builder/actions";

const RecommendationItem = ({ product, onSelect }) => {
    return (
        <Grid.Row>
            <Grid.Column width={1}>
                <Checkbox onChange={() => onSelect(product)} />
            </Grid.Column>
            <Grid.Column width={15}>
                <Header as="h4">
                    {product.product}
                    <Header.Subheader>
                        {product.product_type}, {product.product_size} |{" "}
                        {formatAmount(product.price)}/Unit | {product.upc}{" "}
                        {product.internal_sku && product.internal_sku !== "0"
                            ? `| ${product.internal_sku}`
                            : ""}
                    </Header.Subheader>
                    {!!product.id && (
                        <Header.Subheader>
                            {product.timestamp
                                ? `Last Ordered: ${format(
                                      parseServerTimestampForClient(product.timestamp),
                                      DateFormat
                                  )} | ${pluralize("Units", product.qty, true)}`
                                : "Never Ordered"}
                        </Header.Subheader>
                    )}
                </Header>
            </Grid.Column>
        </Grid.Row>
    );
};

export const InvoiceRecommendationListContainer = ({ customer }) => {
    const {
        isAiActive,
        productsForDay,
        isLoadingProductsForDay,
        errorProductsForDay,
        showModal,
        isTieredPricingActive,
        productPriceTiers,
    } = useSelector(({ auth, invoiceRecommendation }) => ({
        isAiActive: isCompanyFeatureActive(auth.user?.company, CompanyFeatures.AI),
        isTieredPricingActive: isCompanyFeatureActive(
            auth?.user?.company,
            CompanyFeatures.TIERED_PRICING
        ),
        productPriceTiers: auth?.user?.company?.productPriceTiers,
        ...invoiceRecommendation,
    }));
    const [selectedProducts, setSelectedProducts] = useState({});
    const dispatch = useDispatch();

    useEffect(() => {
        if (isAiActive) {
            dispatch(loadInvoiceRecommendation());
        }
    }, [isAiActive]);

    const customerPriceTier = isTieredPricingActive
        ? productPriceTiers?.find(tier => tier.id === customer?.product_price_tier_id)
        : null;

    const addSelectionToInvoice = () => {
        dispatch(addInvoiceBuilderItemsFromRecommendation(Object.values(selectedProducts)));
        dispatch(toggleRecommendationModal());
    };

    if (!isAiActive) {
        return null;
    }

    return (
        <Modal open={showModal} size="small" onClose={() => dispatch(toggleRecommendationModal())}>
            <Modal.Header>Invoice Recommendation</Modal.Header>

            <Modal.Content>
                {isLoadingProductsForDay ? (
                    <Segment>
                        <Dimmer active inverted>
                            <Loader />
                        </Dimmer>
                        <div className="hp-15 sp-15"></div>
                    </Segment>
                ) : productsForDay?.length ? (
                    <div>
                        Based on your usage history, we think these are the products you are most
                        likely to be invoicing right now.
                        <Divider hidden />
                        <Grid>
                            {productsForDay.map(prod => {
                                const product = transformProductToItem(prod, {
                                    customerId: customer?.id,
                                    isTieredPricingActive,
                                    customerPriceTier,
                                });

                                return (
                                    <RecommendationItem
                                        key={prod.id}
                                        product={product}
                                        onSelect={product => {
                                            const list = { ...selectedProducts };
                                            if (selectedProducts[product.id]) {
                                                delete list[product.id];
                                                setSelectedProducts(list);
                                            } else {
                                                setSelectedProducts({
                                                    ...list,
                                                    [product.id]: product,
                                                });
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Grid>
                    </div>
                ) : (
                    <Message
                        error
                        content={
                            errorProductsForDay ||
                            "Sorry, we could not generate a recommendation for you at the moment"
                        }
                    />
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button content={"Close"} onClick={() => dispatch(toggleRecommendationModal())} />
                <Button
                    positive
                    icon={"plus"}
                    content={"Add to invoice"}
                    onClick={addSelectionToInvoice}
                    disabled={isEmpty(selectedProducts)}
                />
            </Modal.Actions>
        </Modal>
    );
};
